<script lang="ts">
const __DsBlock = {
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input.name',
	description: '$t:blocks.input.description',
	type: 'input',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => {
			const types = ['text'];
			if (isPrimitiveTypeString(primitiveType)) types.push('identifier');
			return types;
		},
		recommendedDisplays: ['$builtin:DisplayFormattedValue'],
	},

	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'type',
			type: 'enum',
			values: ['text', 'password', 'email', 'number', 'tel', 'url', 'date', 'time', 'datetime-local', 'month', 'week'],
		},
		{
			name: 'placeholder',
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'leadingIcon',
			block: BuiltinBlockIds.InputIcon,
		},
		{
			name: 'trailingIcon',
			block: BuiltinBlockIds.InputIcon,
		},
		{
			name: 'disabled',
		},
		{
			name: 'readonly',
		},
		{
			name: 'disabledNotice',
		},
		{
			name: 'loading',
		},
		{
			name: 'block',
		},
		{
			name: 'autocomplete',
		},
		{ name: 'modelModifiers', hidden: true },
	],
}
</script>
<script lang="ts" setup>
import type { InputProps } from '#layers/ui';
import type { InputBlockProps } from '~/types/input-block';
import { reactivePick } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';

import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { isPrimitiveTypeString } from '#layers/data-model';
import { useTranslationUtils } from '#layers/i18n';
import { useDisabledNotice } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables';

type InputType = 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'date' | 'time' | 'datetime-local' | 'month' | 'week';

const props = withDefaults(defineProps<Props>(), {
	block: true,
});

interface Props extends InputBlockProps, Pick<
	InputProps<any>,
    'placeholder' | 'color' | 'variant' | 'size' | 'leadingIcon' | 'trailingIcon' | 'loading' | 'autocomplete' | 'modelModifiers'
> {
	type?: InputType;

	avatar?: string;

	/**
	 * Render the input full width
	 */
	block?: boolean;
}

const model = defineModel<string>();

;

const forwarded = reactivePick(props, 'type', 'color', 'variant', 'size', 'leadingIcon', 'trailingIcon', 'loading', 'block', 'autocomplete');
const { translateString } = useTranslationUtils();
const translatedPlaceholder = computed(() => translateString(props.placeholder));
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInput
			v-bind="forwarded"
			v-model="modelValue"
			:model-modifiers="modelModifiers ?? {}"
			:placeholder="translatedPlaceholder"
			:readonly
			:aria-readonly="readonly"
			:disabled
			:avatar="avatar ? {
				src: avatar,
			} : undefined"
			class="min-w-0"
		/>
	</DTooltip>
</template>
